
import React, { useEffect } from 'react';
import { ChevronRight, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NavigationItem as NavItemType } from '../types/navigation';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { fetchChatbotsIfNeeded, selectChatbots, selectChatbotsLoading } from '@/store/slices/chatbots/chatbotsSlice';

interface BotInboxesNavigationItemProps {
  item: NavItemType;
  isSecondPanelCollapsed: boolean;
  expandedItems: string[];
  toggleExpanded: (itemTitle: string) => void;
  hasActiveChild?: (children: NavItemType[]) => boolean;
  isItemActive: (path: string) => boolean;
  navigate: (path: string) => void;
  filterMenuItems: (items: NavItemType[]) => NavItemType[];
}

const BotInboxesNavigationItem = ({
  item,
  isSecondPanelCollapsed,
  expandedItems,
  toggleExpanded,
  hasActiveChild,
  isItemActive,
  navigate,
  filterMenuItems,
}: BotInboxesNavigationItemProps) => {
  const dispatch = useAppDispatch();
  
  // Get chatbots from Redux
  const chatbots = useAppSelector(selectChatbots);
  const chatbotsLoading = useAppSelector(selectChatbotsLoading);

  // Load data when component mounts - conditionally fetch
  useEffect(() => {
    dispatch(fetchChatbotsIfNeeded());
  }, [dispatch]);

  // Create dynamic children based on chatbots only
  const dynamicChildren: NavItemType[] = [
    // Chatbot inboxes
    ...chatbots.map(chatbot => ({
      title: `${chatbot.name}`,
      path: `/home/inbox/bot/${chatbot.id}`,
      icon: Bot,
    }))
  ];

  const isExpanded = expandedItems.includes(item.title);
  const hasActiveDynamicChild = dynamicChildren.some(child => isItemActive(child.path));

  return (
    <div>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className={`w-full flex items-center rounded-lg transition-all duration-300 group ${
                isSecondPanelCollapsed ? 'justify-center p-2' : 'justify-between px-4 py-2'
              } ${
                hasActiveDynamicChild
                  ? 'bg-primary/5 text-primary shadow-sm hover:shadow-md'
                  : 'hover:bg-primary/5'
              }`}
              onClick={() => toggleExpanded(item.title)}
            >
              <div className={`flex items-center ${isSecondPanelCollapsed ? 'justify-center' : 'gap-3'}`}>
                {item.icon && (
                  <div className="flex items-center justify-center w-5">
                    <item.icon className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
                  </div>
                )}
                {!isSecondPanelCollapsed && (
                  <span className="font-medium transition-colors group-hover:text-primary">
                    {item.title}
                  </span>
                )}
              </div>
              {!isSecondPanelCollapsed && (
                <ChevronRight className={`h-4 w-4 transition-all duration-300 ${
                  isExpanded ? 'rotate-90' : ''
                } group-hover:text-primary`} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            className="z-[60] bg-white shadow-lg"
            sideOffset={12}
          >
            <p>{item.title}</p>
            <p className="text-xs text-muted-foreground">
              Click to {isExpanded ? 'collapse' : 'expand'}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {isExpanded && !isSecondPanelCollapsed && (
        <div className="ml-8 mt-1 space-y-1 animate-accordion-down">
          {dynamicChildren.length === 0 && chatbotsLoading && (
            <div className="px-4 py-2 text-sm text-muted-foreground flex items-center gap-2">
              <div className="animate-spin w-3 h-3 border-2 border-muted-foreground border-t-transparent rounded-full"></div>
              Loading chatbot profiles...
            </div>
          )}
          
          {dynamicChildren.length === 0 && !chatbotsLoading && (
            <div className="px-4 py-2 text-sm text-muted-foreground">
              No chatbot profiles available
            </div>
          )}

          {filterMenuItems(dynamicChildren).map((child: NavItemType) => (
            <TooltipProvider key={child.title} delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className={`w-full flex items-center justify-start text-sm px-4 py-2 rounded-lg transition-all duration-300 group
                      ${isItemActive(child.path)
                        ? 'bg-primary/5 text-primary shadow-sm hover:shadow-md'
                        : 'hover:bg-primary/5'}`}
                    onClick={() => navigate(child.path)}
                  >
                    <div className="flex items-center gap-3">
                      {child.icon && (
                        <div className="flex items-center justify-center w-4">
                          <child.icon className="h-3 w-3" />
                        </div>
                      )}
                      <span className="transition-colors group-hover:text-primary">
                        {child.title}
                      </span>
                    </div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="z-[60] bg-white shadow-lg"
                  sideOffset={12}
                >
                  <p>{child.title}</p>
                  <p className="text-xs text-muted-foreground">Click to navigate</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      )}
    </div>
  );
};

export default BotInboxesNavigationItem;
